import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpContext, HttpErrorResponse} from '@angular/common/http';
import {map} from 'rxjs';
import {ApiResponse} from '../../auth/auth.models';
import {buildApiUrl} from '../api/api-url';
import {isLocallyHandledApiError, LocalApiErrorCode, LOCALLY_HANDLED_API_ERROR_CODES} from '../errors/local-api-error';
import {ItemPriceDto, mapItemPrice, ItemPrice} from '../prices/currency.service';
import {UnitOptionDto} from '../../items/items.service';

export type PackagingCorrectionKind = 'item' | 'need' | 'purchase';
export interface PackagingCorrectionHistory { operationId: string; correctedAt: string; actorId: string | null;
  actorName: string | null; sourceUnitId: string; targetUnitId: string }
export interface PackagingCorrectionTarget {kind: PackagingCorrectionKind; id: string; targetUnitId: string}
interface PackagingCorrectionRecordDto {
  kind: PackagingCorrectionKind; id: string; name: string; unitId: string; quantity: number;
  packagingQuantity: number | null; packagingUnitId: string | null; remainingContent: number | null; price: ItemPriceDto | null;
}
export interface PackagingCorrectionRecord extends Omit<PackagingCorrectionRecordDto, 'price'> {price: ItemPrice | null}
interface PackagingCorrectionTrackingDto {
  productGroupId: string; containerId: string; containerName: string; unitId: string;
  packagingQuantity: number | null; packagingUnitId: string | null;
  minimumQuantity: number | null; desiredQuantity: number | null; version: number;
}
export interface PackagingCorrectionTracking extends PackagingCorrectionTrackingDto {}
interface PackagingCorrectionPreviewDto {
  target: PackagingCorrectionTarget; previewToken: string; records: PackagingCorrectionRecordDto[];
  purchaseChoices: PackagingCorrectionRecordDto[]; tracking?: PackagingCorrectionTrackingDto[];
}
export interface PackagingCorrectionPreview extends Omit<PackagingCorrectionPreviewDto, 'records' | 'purchaseChoices' | 'tracking'> {
  records: PackagingCorrectionRecord[]; purchaseChoices: PackagingCorrectionRecord[];
  tracking: PackagingCorrectionTracking[];
}
export const PACKAGING_ERROR_CODES: readonly LocalApiErrorCode[] = ['PackagingCorrectionUnsupported',
  'PackagingCorrectionStale', 'PackagingCorrectionGroupConflict', 'PackagingCorrectionTrackingConflict', 'PackagingCorrectionAmbiguous',
  'PackagingCorrectionAccessDenied', 'PackagingCorrectionPriceBasis', 'PackagingNotSupported', 'PackagingContentLocked'];

function mapPreview(dto: PackagingCorrectionPreviewDto): PackagingCorrectionPreview {
  const record = (value: PackagingCorrectionRecordDto): PackagingCorrectionRecord => ({...value, price: mapItemPrice(value.price)});
  return {target: {...dto.target}, previewToken: dto.previewToken, records: dto.records.map(record),
    purchaseChoices: dto.purchaseChoices.map(record), tracking: (dto.tracking ?? []).map(value => ({
      productGroupId: value.productGroupId, containerId: value.containerId, containerName: value.containerName,
      unitId: value.unitId, packagingQuantity: value.packagingQuantity, packagingUnitId: value.packagingUnitId,
      minimumQuantity: value.minimumQuantity, desiredQuantity: value.desiredQuantity, version: value.version,
    }))};
}

export function packagingErrorKey(error: unknown): string | null {
  return isLocallyHandledApiError(error, PACKAGING_ERROR_CODES)
    ? `packagingCorrection.errors.${(error as HttpErrorResponse).error.error.code}` : null;
}

@Injectable({providedIn: 'root'})
export class PackagingCorrectionService {
  private readonly http = inject(HttpClient);
  private readonly options = {context: new HttpContext().set(LOCALLY_HANDLED_API_ERROR_CODES, PACKAGING_ERROR_CODES)};
  history(kind: PackagingCorrectionKind, id: string) {
    return this.http.get<ApiResponse<PackagingCorrectionHistory[]>>(
      buildApiUrl(`/v1/me/packaging-corrections/history/${kind}/${id}`), this.options)
      .pipe(map(response => response.data!.map(event => ({...event}))));
  }

  units() { return this.http.get<ApiResponse<UnitOptionDto[]>>(buildApiUrl('/v1/me/packaging-corrections/units'))
    .pipe(map(response => response.data!)); }
  preview(target: PackagingCorrectionTarget) {
    return this.http.post<ApiResponse<PackagingCorrectionPreviewDto>>(buildApiUrl('/v1/me/packaging-corrections/preview'), target, this.options)
      .pipe(map(response => mapPreview(response.data!)));
  }
  confirm(preview: PackagingCorrectionPreview, operationId: string) {
    return this.http.post<ApiResponse<PackagingCorrectionPreviewDto>>(buildApiUrl('/v1/me/packaging-corrections'),
      {target: preview.target, previewToken: preview.previewToken, operationId}, this.options)
      .pipe(map(response => mapPreview(response.data!)));
  }
}
